import React, { useState, useEffect } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ChevronDown,
  ChevronUp,
  Edit,
  FileText,
  Mail,
  Phone,
  Plus,
  Trash2,
} from 'lucide-react';
import CountdownTimer from './CountdownTimer';
import { exportLeadToPdf } from './pdfExport';

const statusBadgeClasses = {
  nieuw: 'bg-blue-100 text-blue-800 border-blue-200',
  in_behandeling: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  afspraak: 'bg-purple-100 text-purple-800 border-purple-200',
  klant_geworden: 'bg-green-100 text-green-800 border-green-200',
  geen_klant_geworden: 'bg-red-100 text-red-800 border-red-200',
};

const statusLabels = {
  nieuw: 'Nieuw',
  in_behandeling: 'In behandeling',
  afspraak: 'Afspraak',
  klant_geworden: 'Klant geworden',
  geen_klant_geworden: 'Geen klant',
};

export const LeadRow = ({
  lead,
  isExpanded,
  leadActions,
  onToggleExpand,
  onUpdateStatus,
  onDeleteLead,
  onEditLead,
  onAddAction,
  onDeleteAction,
  onCommissionChange,
  onCommissionBlur,
  getActionIcon,
  formatDate,
  formatActionDate,
  formatCurrency,
}) => {
  const [newAction, setNewAction] = useState({ action_type: '', note: '' });
  const [isAddingAction, setIsAddingAction] = useState(false);

  // Reset draft when the row is collapsed so re-expanding starts fresh.
  useEffect(() => {
    if (!isExpanded) {
      setNewAction({ action_type: '', note: '' });
    }
  }, [isExpanded]);

  const handleAddAction = async () => {
    if (!newAction.action_type) return;
    setIsAddingAction(true);
    try {
      await onAddAction(lead.id, newAction);
      setNewAction({ action_type: '', note: '' });
    } finally {
      setIsAddingAction(false);
    }
  };

  return (
    <React.Fragment>
      <TableRow
        data-testid={`lead-row-${lead.id}`}
        className={isExpanded ? 'bg-slate-50' : ''}
      >
        <TableCell>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleExpand(lead.id)}
            className="p-1 h-6 w-6"
            data-testid={`expand-lead-${lead.id}`}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </TableCell>
        <TableCell>
          <div className="text-sm">{formatDate(lead.created_at)}</div>
        </TableCell>
        <TableCell>
          <CountdownTimer createdAt={lead.created_at} />
        </TableCell>
        <TableCell>
          <div className="space-y-2">
            <div className="text-sm font-medium">{lead.first_name} {lead.last_name}</div>
            <div className="text-xs text-slate-500">{lead.customer_type}</div>
            <div className="text-xs text-slate-600">{lead.address}, {lead.postcode}</div>
          </div>
        </TableCell>
        <TableCell>
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-sm">
              <Mail className="w-3 h-3 flex-shrink-0" />
              <span className="break-all text-xs">{lead.email}</span>
            </div>
            <div className="flex items-center gap-1 text-sm">
              <Phone className="w-3 h-3 flex-shrink-0" />
              <span>{lead.phone}</span>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="space-y-1">
            <div className="text-xs">
              <div className="font-medium">{lead.current_providers?.join(', ') || 'Niet ingevuld'}</div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="text-sm space-y-1">
            <div>{(lead.services || lead.services_needed || []).join(', ') || 'Niet ingevuld'}</div>
            {(lead.services || []).includes('Mobiel') && lead.mobile_count > 0 && (
              <div className="text-xs text-slate-500">Mobiel: {lead.mobile_count}x</div>
            )}
            {(lead.services || []).includes('TV') && lead.tv_decoder_count > 0 && (
              <div className="text-xs text-slate-500">TV: {lead.tv_decoder_count}x</div>
            )}
          </div>
        </TableCell>
        <TableCell>
          <Select value={lead.status} onValueChange={(value) => onUpdateStatus(lead.id, value)}>
            <SelectTrigger className="w-auto border-0 p-0 h-auto">
              <Badge className={`${statusBadgeClasses[lead.status] || statusBadgeClasses.nieuw} text-xs px-2 py-1`}>
                {statusLabels[lead.status] || lead.status}
              </Badge>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nieuw">Nieuw</SelectItem>
              <SelectItem value="in_behandeling">In behandeling</SelectItem>
              <SelectItem value="afspraak">Afspraak</SelectItem>
              <SelectItem value="klant_geworden">Klant geworden</SelectItem>
              <SelectItem value="geen_klant_geworden">Geen klant</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <Select
            value={lead.assigned_to || 'niemand'}
            onValueChange={(value) => onUpdateStatus(lead.id, lead.status, value === 'niemand' ? null : value)}
          >
            <SelectTrigger className="w-28 text-xs">
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="niemand">Niemand</SelectItem>
              <SelectItem value="Jelle">Jelle</SelectItem>
              <SelectItem value="Wim">Wim</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          {lead.last_action ? (
            <div className="flex items-center gap-2">
              {getActionIcon(lead.last_action)}
              <div>
                <div className="text-xs font-medium">{lead.last_action}</div>
                <div className="text-xs text-slate-400">
                  {lead.last_action_at && formatActionDate(lead.last_action_at)}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-xs text-slate-400">Geen actie</span>
          )}
        </TableCell>
        <TableCell className="text-center">
          {lead.commission && lead.commission > 0 ? (
            <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mx-auto" title={`€${lead.commission.toFixed(2)}`}>
              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          ) : (
            <div className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
              <span className="text-slate-400 text-xs">-</span>
            </div>
          )}
        </TableCell>
        <TableCell>
          <div className="space-y-1">
            {lead.energy_interest ? (
              <div>
                <div className="text-xs font-medium text-green-700">Ja</div>
                {lead.current_energy_provider && (
                  <div className="text-xs text-slate-500">{lead.current_energy_provider}</div>
                )}
              </div>
            ) : (
              <div className="text-xs text-slate-500">Nee</div>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEditLead(lead)}
              className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
              data-testid={`edit-lead-${lead.id}`}
              title="Bewerken"
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => exportLeadToPdf(lead)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              data-testid={`export-lead-${lead.id}`}
              title="Exporteer naar PDF"
            >
              <FileText className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDeleteLead(lead.id, `${lead.first_name} ${lead.last_name}`)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid={`delete-lead-${lead.id}`}
              title="Verwijderen"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow className="bg-slate-50 border-b-2 border-slate-200">
          <TableCell colSpan={13} className="p-4">
            <div className="space-y-4">
              {/* Commission input */}
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <div className="flex items-center gap-4">
                  <div>
                    <Label className="text-xs mb-1 block text-green-700 font-medium">Commissie (€)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={lead.commission || ''}
                        onChange={(e) => onCommissionChange(lead.id, e.target.value)}
                        onBlur={(e) => onCommissionBlur(lead.id, e.target.value)}
                        className="w-28 h-8 text-sm"
                        data-testid={`commission-input-${lead.id}`}
                      />
                      {lead.commission && lead.commission > 0 && (
                        <span className="text-green-600 text-sm font-medium">
                          {formatCurrency(lead.commission)}
                        </span>
                      )}
                    </div>
                  </div>
                  {lead.commission_date && (
                    <div className="text-xs text-green-600">
                      Ingevuld op: {formatActionDate(lead.commission_date)}
                    </div>
                  )}
                </div>
              </div>

              {/* Add new action form */}
              <div className="flex gap-3 items-end bg-white p-3 rounded-lg border">
                <div className="flex-1 max-w-[200px]">
                  <Label className="text-xs mb-1 block">Actie type</Label>
                  <Select
                    value={newAction.action_type}
                    onValueChange={(value) => setNewAction((prev) => ({ ...prev, action_type: value }))}
                  >
                    <SelectTrigger className="text-sm" data-testid={`action-type-select-${lead.id}`}>
                      <SelectValue placeholder="Selecteer actie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Gebeld">Gebeld</SelectItem>
                      <SelectItem value="Gemaild">Gemaild</SelectItem>
                      <SelectItem value="Pakt niet op">Pakt niet op</SelectItem>
                      <SelectItem value="Terugbellen">Terugbellen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-xs mb-1 block">Notitie (optioneel)</Label>
                  <Input
                    placeholder="Voeg een notitie toe..."
                    value={newAction.note}
                    onChange={(e) => setNewAction((prev) => ({ ...prev, note: e.target.value }))}
                    className="text-sm"
                    data-testid={`action-note-input-${lead.id}`}
                  />
                </div>
                <Button
                  onClick={handleAddAction}
                  disabled={isAddingAction || !newAction.action_type}
                  size="sm"
                  className="flex items-center gap-1"
                  data-testid={`add-action-button-${lead.id}`}
                >
                  <Plus className="w-4 h-4" />
                  Toevoegen
                </Button>
              </div>

              {/* Actions history */}
              <div>
                <h4 className="text-sm font-semibold mb-2 text-slate-700">Actiegeschiedenis</h4>
                {(leadActions || []).length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Nog geen acties geregistreerd</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {(leadActions || []).map((action) => (
                      <div
                        key={action.id}
                        className="flex items-start gap-3 p-2 bg-white rounded border hover:border-slate-300 transition-colors"
                      >
                        <div className="mt-0.5">{getActionIcon(action.action_type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{action.action_type}</span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs text-slate-500">{action.created_by_name}</span>
                          </div>
                          <div className="text-xs text-slate-400">{formatActionDate(action.created_at)}</div>
                          {action.note && <p className="text-sm text-slate-600 mt-1">{action.note}</p>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteAction(lead.id, action.id)}
                          className="text-slate-400 hover:text-red-600 p-1 h-6 w-6"
                          data-testid={`delete-action-${action.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};
